import React, { useEffect, useState } from "react";
import { toast } from "../hooks/use-toast";
import { api, publicApi } from "../lib/api";
import { ArrowUpRight, Check } from "lucide-react";

const focusExtras = ["STEM", "Entrepreneurship", "Leadership", "Digital Skills", "Innovation", "Research", "Girls' Education", "Unrestricted Funding"];

const Partner = () => {
  const [types, setTypes] = useState(["Funder / Foundation", "Government", "NGO / Nonprofit", "Other"]);
  const [focusOpts, setFocusOpts] = useState(focusExtras);
  const [form, setForm] = useState({ org: '', name: '', email: '', phone: '', website: '', type: 'Funder / Foundation', focus: [], focus_other: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => { publicApi.get('/options').then(({ data }) => { if (data.partnership_types) setTypes(data.partnership_types); if (data.focus_areas) setFocusOpts(data.focus_areas); }).catch(() => {}); }, []);

  const upd = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };
  const toggle = (v) => setForm(f => ({ ...f, focus: f.focus.includes(v) ? f.focus.filter(x => x !== v) : [...f.focus, v] }));

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.org.trim()) err.org = 'Required';
    if (!form.name.trim()) err.name = 'Required';
    if (!form.email.trim()) err.email = 'Required';
    if (form.focus.includes('Other') && !form.focus_other.trim()) err.focus_other = 'Please specify';
    setErrors(err);
    if (Object.keys(err).length) return;
    setSubmitting(true);
    try {
      await api.post('/applications/partner', form);
      toast({ title: 'Inquiry sent', description: 'We’ll get back within 3 business days.' });
      setForm({ org: '', name: '', email: '', phone: '', website: '', type: types[0], focus: [], focus_other: '', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Please try again.');
      toast({ title: 'Submission failed', description: msg });
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="section-eyebrow text-[#0d2a20]/60 mb-6">Partner With Us</div>
        <h1 className="hero-title text-[52px] md:text-[96px] leading-[0.95] max-w-[1200px]">Build the <span className="font-serif-i text-[#e0684d]">ecosystem</span> with us.</h1>
        <p className="mt-8 max-w-[820px] text-[17px] leading-relaxed text-[#0d2a20]/80">We believe the private sector has a critical role to play in educational transformation.</p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 grid md:grid-cols-5 gap-10">
        <ul className="md:col-span-2 space-y-3 text-[15px] text-[#0d2a20]/85">
          {['Funders & foundations backing capacity','Government & multilateral collaboration','Peer NGOs building the same future','Bespoke partnerships that go beyond funding'].map(x => (
            <li key={x} className="flex gap-3 items-start"><span className="text-[#e0684d]">→</span>{x}</li>
          ))}
        </ul>
        <form onSubmit={submit} className="md:col-span-3 rounded-lg p-8 space-y-4" style={{ background: '#fbf8ef' }}>
          {[['org','Organisation name *',true],['name','Contact name *',true],['email','Email *',true],['phone','Phone',false],['website','Website',false]].map(([k,l,req]) => (
            <div key={k}>
              <label className="text-[12px] text-[#0d2a20]/70">{l}</label>
              <input required={req} value={form[k]} onChange={e => upd(k, e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
              {errors[k] && <div className="text-[12px] text-[#7a1f13] mt-1">{errors[k]}</div>}
            </div>
          ))}
          <div>
            <label className="text-[12px] text-[#0d2a20]/70">Partnership type *</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {types.map(t => (
                <button key={t} type="button" onClick={() => upd('type', t)} className={`px-3 py-1.5 rounded-md text-[13px] ${form.type === t ? 'bg-[#0f3d2e] text-[#f4efe4]' : 'bg-white border'}`} style={form.type !== t ? { borderColor: 'rgba(13,42,32,0.15)' } : {}}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[12px] text-[#0d2a20]/70">Focus areas of interest</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {[...focusOpts, 'Other'].map(f => {
                const sel = form.focus.includes(f);
                return (
                  <button key={f} type="button" onClick={() => toggle(f)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[13px] ${sel ? 'bg-[#e0684d] text-white' : 'bg-white border'}`} style={!sel ? { borderColor: 'rgba(13,42,32,0.15)' } : {}}>
                    {sel && <Check size={12} />} {f}
                  </button>
                );
              })}
            </div>
            {form.focus.includes('Other') && (
              <div className="mt-2">
                <label className="text-[12px] text-[#0d2a20]/70">Please specify *</label>
                <input value={form.focus_other} onChange={e => upd('focus_other', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
                {errors.focus_other && <div className="text-[12px] text-[#7a1f13] mt-1">{errors.focus_other}</div>}
              </div>
            )}
          </div>
          <div>
            <label className="text-[12px] text-[#0d2a20]/70">Message (optional)</label>
            <textarea value={form.message} onChange={e => upd('message', e.target.value)} rows={5} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
          </div>
          <button disabled={submitting} className="inline-flex items-center gap-2 px-5 py-3 rounded-md btn-coral text-[14px] disabled:opacity-60">{submitting ? 'Sending…' : 'Send inquiry'} <ArrowUpRight size={16} /></button>
        </form>
      </section>
    </div>
  );
};

export default Partner;
